import { apiSlice } from './apiSlice';

// ─── Types ───────────────────────────────────────────────────────────

export interface HeatingConfig {
  minC?: number;
  maxC?: number;
  ventilationEnabled?: boolean;
  ventilationMaxC?: number;
}

export interface LightingConfig {
  targetHours?: number;
}

export interface WateringConfig {
  subtype?: string;
  triggerMoisturePct?: number;
  depthMm?: number;
  targetMoisturePct?: number;
}

export type AutomationConfig = HeatingConfig | LightingConfig | WateringConfig;

export interface AutomationTargetState {
  enabled: boolean;
  properties?: AutomationConfig | null;
}

export interface AutomationStatus {
  heating: Record<string, AutomationTargetState>;
  watering: Record<string, AutomationTargetState>;
  lighting: Record<string, AutomationTargetState>;
}

export type AutomationType = 'heating' | 'watering' | 'lighting';

interface ToggleAutomationRequest {
  saveId: string;
  automationType: AutomationType;
  enabled: boolean;
  targetId: string;
  properties?: AutomationConfig;
  date?: string;
}

interface AutomationStatusRequest {
  plotId: string;
  saveId: string;
}

// ─── API slice (save-scoped after Wave 5.2) ─────────────────────────

export const automationApi = apiSlice
  .enhanceEndpoints({ addTagTypes: ['SimTimeseries'] })
  .injectEndpoints({
  endpoints: (builder) => ({
    getAutomationStatus: builder.query<AutomationStatus, AutomationStatusRequest>({
      query: ({ plotId, saveId }) =>
        `/saves/${saveId}/automation/status?plotId=${plotId}`,
      transformResponse: (response: { success: boolean; data: AutomationStatus }) =>
        response.data,
      providesTags: (_r, _e, { plotId }) => [{ type: 'Automation' as const, id: plotId }],
    }),
    toggleAutomation: builder.mutation<void, ToggleAutomationRequest>({
      query: ({ saveId, ...body }) => ({
        url: `/saves/${saveId}/automation/toggle`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { saveId }) => [
        { type: 'Automation' as const },
        'SimTimeseries',
        { type: 'SaveV2' as const, id: saveId },
        { type: 'SaveV2' as const, id: `world-${saveId}` },
      ],
    }),
  }),
});

export const { useGetAutomationStatusQuery, useToggleAutomationMutation } = automationApi;
